// Build-time variant swap (AHD-6121): in production this resolves to
// `posthog-js/dist/module.slim` — the SessionRecording extension class is
// dropped, saving ~30 KiB gzipped. In every other environment (preview /
// preflight / feature-branch / local dev / agent-browser tests) it resolves
// to the full `posthog-js`, whose built-in `lazy-recorder` mechanism fetches
// the rrweb recorder script from PostHog's CDN at runtime when replays are
// enabled. See `useSlimPosthog` in next.config.ts.
import posthogDefault from "posthog-js-default"

// The build-time alias above breaks webpack module dedup: in production the
// slim resolved path gets bundled per chunk that imports `lib/posthog.ts`,
// so consumers wind up with separate posthog instances — only one of which
// `initPostHog()` ever configures. Symptom: any client-side hook that reads
// flags via the module-imported `posthog` got `undefined` for every flag in
// prod while `window.posthog` (the one that posthog-js auto-attached) had
// the right values. Pin a single instance on globalThis so duplicated copies
// of this module export the same reference. See PR #1166 / AHD-6477.
const POSTHOG_SINGLETON_KEY = "__ahead_posthog_singleton__"
type PosthogSingletonHost = { [POSTHOG_SINGLETON_KEY]?: typeof posthogDefault }
const singletonHost = globalThis as PosthogSingletonHost
if (!singletonHost[POSTHOG_SINGLETON_KEY]) {
  singletonHost[POSTHOG_SINGLETON_KEY] = posthogDefault
}
const posthog = singletonHost[POSTHOG_SINGLETON_KEY] as typeof posthogDefault
import {
  isTestMode,
  getTestId,
} from "@ahead-health/core/system-checks/testMode"
import { logTestSessionStarted } from "@ahead-health/core/analytics/testIntegration"
import { identifyFromUrlPh } from "@ahead-health/core/analytics/identifyFromUrlPh"
import { IS_DEV, IS_PRODUCTION } from "@/lib/env"
import { stripPiiForPostHog } from "@ahead-health/core/analytics/posthogPii"
import {
  isAnalyticsTest,
  isPreConsentedAtInit,
  resolveConsentCookieDomain,
} from "@ahead-health/core/ui/cookieConsent"

export const POSTHOG_KEY = "phc_vO5cUVN2NR0yjaisUsANGlFmv9Gur0iUa06aH7KufiN"
// Direct host for server-to-server calls (posthog-server.ts).
// Browser calls go through the /ingest reverse proxy — see next.config.ts rewrites.
export const POSTHOG_HOST = "https://eu.i.posthog.com"
export const POSTHOG_PROXY_PATH = "/ingest"

/**
 * Environment name for PostHog `$environment` super property — attached to
 * every event so we can filter preview/dev traffic out of production dashboards.
 * Configure the filter in PostHog (Project settings → Data → Event filters).
 */
function getEnvironment(): string {
  if (typeof window === "undefined") return "server"
  return process.env.NEXT_PUBLIC_VERCEL_ENV || "development"
}

/** Testing shortcut — `?acceptCookies=1` in the URL opts the visitor in
 * without requiring a banner click. Used by validation / integration tests
 * that need PostHog capture to fire without scripting the consent UI. */
function hasAcceptCookiesParam(): boolean {
  if (typeof window === "undefined") return false
  return (
    new URLSearchParams(window.location.search).get("acceptCookies") === "1"
  )
}

export function initPostHog(
  experimentId?: string,
  bootstrapFeatureFlags?: Record<string, string | boolean>
) {
  if (typeof window === "undefined") return
  if (posthog.__loaded) return

  // Session replay policy (AHD-6121 / AHD-6306):
  //  - Production → no replays (slim build can't record anyway).
  //  - Anywhere else → replays on, so testers can debug issues on previews,
  //    preflight, and agent-browser sessions.
  // On non-production builds the aliased import resolves to the full
  // posthog-js bundle, whose `disable_session_recording: false` will trigger
  // PostHog to fetch the recorder script (`posthog-recorder.js`) from
  // `/ingest/static/...` (proxied to eu-assets.i.posthog.com — see the
  // rewrites in next.config.ts) and start rrweb.
  const replayEnabled = !IS_PRODUCTION

  // Resolved once at init so `loaded` and the `persistence` config agree.
  // posthog-js writes persistence storage as soon as a captured event flushes,
  // so starting in `memory` for an already-opted-in visitor would lose the
  // distinct_id between the first and second pageview.
  const preConsented = isPreConsentedAtInit()

  // Test mode keeps replays unmasked so browser tests can still read the page.
  const inTestMode = isTestMode()

  posthog.init(POSTHOG_KEY, {
    // Reverse proxy via Next.js rewrites — avoids ad-blocker drops (~15-30%
    // of traffic) and keeps PostHog out of the CSP. ui_host points at the real
    // PostHog Cloud region so toolbar/links open the right project.
    api_host: POSTHOG_PROXY_PATH,
    ui_host: "https://eu.posthog.com",

    // Bootstrap with the experiment ID cookie for consistent variant assignment.
    // The proxy sets this cookie on first visit (functional, no consent needed).
    // PostHog uses this as the distinct_id for feature flag evaluation.
    ...(experimentId && {
      bootstrap: {
        distinctID: experimentId,
        featureFlags: bootstrapFeatureFlags ?? {},
      },
    }),

    // Pageviews — PostHog's SPA tracker listens to history pushState /
    // replaceState / popstate and fires `$pageview` on every route change,
    // same as the old manual `usePathname` effect. Using it lets the
    // provider stay free of `usePathname()`, which under Next 16
    // `cacheComponents: true` would otherwise bail the entire wrapped
    // subtree (i.e. every page body) to per-request streaming.
    // Ref: posthog-js config — capture_pageview "history_change".
    capture_pageview: "history_change",
    capture_pageleave: true,

    // Autocapture clicks, inputs, form submissions
    autocapture: true,

    // Web Vitals — disabled (AHD-6306). posthog-js fetched `web-vitals.js`
    // directly from eu-assets.i.posthog.com (not through the /ingest proxy)
    // and emitted ~21 KiB of monthly `$web_vitals` events. Performance
    // monitoring lives in Lighthouse / Search Console / WebPageTest now.
    capture_performance: {
      web_vitals: false,
      web_vitals_attribution: false,
    },

    // Session recording — off on the production hostname, on everywhere
    // else (see `replayEnabled` above). In production the build-time alias
    // gives us slim posthog with no SessionRecording extension class, so
    // even if this flag were accidentally false there's no recorder to
    // start. In non-production the full SDK is loaded and PostHog will
    // fetch the recorder script lazily when this is `false`.
    disable_session_recording: !replayEnabled,

    // Scrub all on-page text from any captured session replay so the marketing
    // site never leaks PII (AHD-7826). Mirrors report-app / booking-flow
    // masking. Belt-and-braces for test mode or an accidental flip of
    // `disable_session_recording`; PostHog masks only <input type=password> by
    // default, and this site's forms collect email, phone, DOB, and insurance
    // info in plain inputs. Test mode stays unmasked (see `inTestMode`).
    mask_all_text: !inTestMode,
    session_recording: inTestMode
      ? undefined
      : { maskAllInputs: true, maskTextSelector: "*", blockSelector: "img" },

    // Disable features we don't use — saves ~25 KiB (surveys.js not loaded)
    disable_surveys: true,

    // Client-side capture rate limit (single token bucket shared by ALL
    // capture paths — autocapture, `history_change` $pageview, $pageleave, and
    // every `track.*` event). posthog-js defaults to 10/s with a 100-event
    // burst; on the heavily-instrumented homepage a legitimate load spike
    // (autocapture + pageview + concurrent third-party/GTM ad tags) briefly
    // exceeds 100 events and posthog-js *drops* the overflow captures, logging
    // "This capture call is ignored due to client rate limiting" (Sentry
    // MARKETING-SITE-15Y). Widen the bucket so real events aren't lost; the
    // limiter still throttles a genuine runaway loop.
    rate_limiting: {
      events_per_second: 25,
      events_burst_limit: 200,
    },

    // Let headless/automated browsers capture events when the test flag is
    // set. Otherwise posthog-js silently drops captures for anything that
    // looks like a bot (navigator.webdriver + UA regex), which breaks our
    // validation scripts. Real bot traffic in production is still filtered
    // because this only flips when ?acceptCookies=1 or ?testId=… is set.
    opt_out_useragent_filter: hasAcceptCookiesParam() || isTestMode(),

    // Privacy
    respect_dnt: true,
    persistence: preConsented ? "localStorage+cookie" : "memory",
    // Share the distinct_id across every Ahead subdomain so a visitor is one
    // PostHog person across www / my / membership / booking instead of getting
    // a fresh anonymous id per subdomain. Use the same helper the consent
    // cookie uses (single source of truth): it returns a domain for BOTH
    // *.aheadhealth.com AND *.aheadhealth.dev (so preflight/staging on .dev
    // stitch too — new.aheadhealth.dev ↔ preflight.aheadhealth.dev share the
    // .aheadhealth.dev apex), and undefined on localhost / *.vercel.app where a
    // cross-subdomain cookie can't stick. The previous `.aheadhealth.com`-only
    // check silently disabled cross-domain identity on every .dev env. (AHD-7887)
    cross_subdomain_cookie: !!resolveConsentCookieDomain(),

    // Drop non-production events before they hit the wire to avoid PostHog
    // Cloud billing for preview / dev / feature-branch / agent-browser
    // traffic (AHD-6306 — ~70-90k events/month). Two carve-outs:
    //  1. `$snapshot` events — keep session replays flowing in non-prod so
    //     testers can debug issues on preview deploys.
    //  2. `?systemCheckId=…` URLs — the `/system-internal/checks/posthog`
    //     browser-test page captures one `system_check` event to verify the
    //     wiring is alive; without this carve-out that test would always
    //     fail outside production.
    // Flag evaluation goes through `/flags/`, not the capture pipeline, so
    // `useFeatureFlagEnabled` still works on previews even with this filter.
    //
    // It also strips customer identity fields from every event's properties
    // (AHD-9528). This runs before the environment filter and applies to EVERY
    // capture path — both analytics wrappers, autocapture, the inline 404
    // beacon, and any future call site — so it is the last line of defence
    // behind `stripPiiForPostHog` in the wrappers themselves. The GTM dataLayer
    // mirror does not pass through here and keeps its full payload.
    before_send: (event) => {
      if (!event) return null
      if (event.properties) {
        event.properties = stripPiiForPostHog(event.properties) ?? {}
      }
      if (IS_PRODUCTION) return event
      if (event.event === "$snapshot") return event
      // Analytics browser tests (`?isAnalyticsTest=1`) verify that capture
      // turns on/off with consent — their events must actually reach PostHog
      // even from preview deploys, where the system server's
      // /api/test/posthog-events endpoint looks them up by testId.
      if (isAnalyticsTest()) return event
      const url = String(event.properties?.$current_url ?? "")
      if (url.includes("systemCheckId=")) return event
      // `?acceptCookies=1` — the pre-release analytics validation harness
      // (scripts/validation/analytics-sentry-events.ts) opts in via this param
      // and asserts every page fires a `$pageview` through the `/ingest` proxy.
      // Same carve-out as the analytics browser tests: without it these events
      // are dropped on preview deploys and the check can never pass.
      if (url.includes("acceptCookies=1")) return event
      return null
    },

    loaded: async (ph) => {
      // Tag every event with the current environment so PostHog can filter
      // preview / dev traffic out of production dashboards.
      ph.register({ $environment: getEnvironment() })

      // Analytics browser tests: stamp every event with the runner's testId
      // so the system server's /api/test/posthog-events endpoint can find
      // exactly this run's events. (Capture itself stays consent-gated —
      // isAnalyticsTest disables the test-mode pre-consent shortcut.)
      if (isAnalyticsTest()) {
        const analyticsTestId = getTestId()
        if (analyticsTestId) ph.register({ testId: analyticsTestId })
      }

      // Skip the privacy opt-out when consent is already settled:
      //  1. Test-mode runs (`?testId=…` / persisted in localStorage) — the
      //     system server's browser tests need capture from the first event.
      //  2. `?acceptCookies=1` — validation / integration test shortcut.
      //  3. Swiss visitors (`ahead-geo=ch`) — nDSG is opt-out, the banner
      //     auto-grants in `cookie-consent.tsx`, so opting out at init only
      //     creates a race window where the navbar's auto `$feature_flag_called`
      //     exposure event is dropped (and then dedup'd from re-firing) before
      //     the deferred banner module remounts and flips opt-in back on.
      //  4. Prior-session consenters — `cc_cookie` already carries the
      //     accepted categories, so capture should resume immediately.
      // GDPR-jurisdiction first-time visitors still start opted out and only
      // capture once they click accept on the banner.
      if (!preConsented) {
        ph.opt_out_capturing()
      } else {
        // Write a _session_started event to ahead-system Firestore with
        // the PostHog session replay URL so the system server UI can link
        // directly to the recording for this test run. The Firestore SDK
        // is lazy-imported inside `logTestSessionStarted` itself as of
        // @ahead-health/core 0.26.30 (AHD-6112), so this static import no
        // longer bloats the homepage chunk.
        const testId = getTestId()
        if (testId) {
          logTestSessionStarted(testId, {
            sessionReplayUrl: ph.get_session_replay_url(),
          })
        }
      }
      if (IS_DEV) {
        ph.set_config({ debug: true })
      }

      // Email→app identity stitching: if this visit landed from an email link
      // carrying ?ph=<person hash>, identify the local instance with it so the
      // session joins the same PostHog person as the server-side email.* events.
      // Runs last so the email hash wins over the experiment-id bootstrap; no-op
      // when there's no valid ph. Pass the local singleton — this app pins its
      // own posthog-js instance, distinct from ahead-core's default singleton.
      identifyFromUrlPh(ph)
    },
  })

  // Own `window.posthog` with THIS instance. Two first-party surfaces can only
  // reach the SDK through the global — the inline 404 beacon
  // (components/analytics/not-found-beacon.tsx) and the feedback toolbar's
  // replay-url reader (@ahead-health/core feedbackToolbar/getPostHogReplayUrl) —
  // and posthog-js's npm build never assigns the global itself (only the
  // `array.js` web snippet does). Without this they bind to whatever
  // `window.posthog` happens to be, which in production is a SEPARATE default
  // instance created by a PostHog web-snippet tag in the GTM container
  // (GTM-M8X7HHCQ) using the same UA token but its own UUIDv7 id and full
  // autocapture — so one visit becomes two PostHog persons, and 404s + replay
  // URLs get attributed to the duplicate instead of this consent-gated,
  // AHD-7887-stitched one. Pinning the global here binds those surfaces to the
  // right person and makes the GTM snippet's `posthog.init(...)` a no-op (this
  // instance is already `__loaded` by the time init() returns). The root fix is
  // removing that GTM tag (AHD-8674); this keeps the surfaces correct and
  // working after it's gone.
  ;(window as unknown as { posthog: typeof posthog }).posthog = posthog

  // `__SV` is the marker the PostHog web snippet uses to detect "this global is
  // already a real, initialized SDK" (`e.__SV || (window.posthog = e, e._i = [],
  // e.init = <stub>, …)`). posthog-js's npm build never sets it, so without this
  // line the GTM snippet doesn't recognise the instance we just pinned: it
  // replaces our real `init` with its stub, and that stub then overwrites every
  // method in its (snippet-version-specific) name list with array-push shims —
  // leaving a `window.posthog` that is neither the real SDK nor a complete stub.
  // Methods the snippet's list omits (e.g. `has_opted_in_capturing`) blow up for
  // any third-party caller. Setting it makes the snippet a true no-op, which is
  // what the paragraph above already assumed. (AHD / MARKETING-SITE-135)
  ;(posthog as unknown as { __SV?: number }).__SV = 1
}

export { posthog }
